/**
 * Compatibility Context
 *
 * Manages compatibility validation and checking between PC components
 * Separated from main AppContext for better performance and organization
 */

import React, { createContext, useContext, useReducer, useEffect, type ReactNode } from 'react'
import type { CPUModel } from '../models/cpu/cpu-model'
import type { MotherboardModel } from '../models/motherboard/motherboard-model'
import type { GPUModel } from '../models/gpu/gpu-model'
import type { MemoryModel } from '../models/memory/memory-model'
import type { StorageModel } from '../models/storage/storage-model'
import type { PSUModel } from '../models/psu/psu-model'
import type { CaseModel } from '../models/case/case-model'
import type { CoolerModel } from '../models/cooler/cpu-cooler-model'
import { UnifiedCompatibilityEngine, type CompatibilityReport } from '../compatibility/engines'

/**
 * Component configuration interface
 */
export interface ComponentConfiguration {
  cpu?: CPUModel
  motherboard?: MotherboardModel
  gpu?: GPUModel[]
  memory?: MemoryModel[]
  storage?: StorageModel[]
  psu?: PSUModel
  case?: CaseModel
  cooler?: CoolerModel
}

/**
 * Compatibility issue severity levels
 */
export type CompatibilityIssueSeverity = 'error' | 'warning' | 'info'

/**
 * Compatibility issue interface
 */
export interface CompatibilityIssue {
  id: string
  type: string
  severity: CompatibilityIssueSeverity
  title: string
  description: string
  components: string[]
  suggestion?: string
  canProceed: boolean
}

/**
 * Compatibility check result interface
 */
export interface CompatibilityCheckResult {
  isCompatible: boolean
  score: number
  issues: CompatibilityIssue[]
  warnings: CompatibilityIssue[]
  recommendations: CompatibilityIssue[]
}

/**
 * Compatibility state interface
 */
export interface CompatibilityState {
  // Current build configuration
  configuration: ComponentConfiguration

  // Compatibility checking
  compatibilityResult: CompatibilityCheckResult | null
  compatibilityReport: CompatibilityReport | null
  isChecking: boolean
  checkError: string | null

  // Auto-check settings
  autoCheck: boolean
  checkOnComponentChange: boolean

  // Validation cache
  validationCache: Map<string, CompatibilityCheckResult>
  reportCache: Map<string, CompatibilityReport>

  // Performance tracking
  lastCheckDuration: number | null
  checksPerformed: number

  // Unified engine instance
  compatibilityEngine: UnifiedCompatibilityEngine
}

/**
 * Compatibility action types
 */
export type CompatibilityAction =
  // Configuration Actions
  | { type: 'SET_COMPONENT'; payload: { componentType: keyof ComponentConfiguration; component: ComponentConfiguration[keyof ComponentConfiguration] } }
  | { type: 'REMOVE_COMPONENT'; payload: { componentType: keyof ComponentConfiguration; componentId?: string } }
  | { type: 'CLEAR_CONFIGURATION' }
  | { type: 'LOAD_CONFIGURATION'; payload: ComponentConfiguration }

  // Compatibility Check Actions
  | { type: 'START_COMPATIBILITY_CHECK' }
  | { type: 'SET_COMPATIBILITY_RESULT'; payload: CompatibilityCheckResult }
  | { type: 'SET_COMPATIBILITY_REPORT'; payload: CompatibilityReport }
  | { type: 'SET_COMPATIBILITY_ERROR'; payload: string }
  | { type: 'CLEAR_COMPATIBILITY_RESULT' }

  // Settings Actions
  | { type: 'SET_AUTO_CHECK'; payload: boolean }
  | { type: 'SET_CHECK_ON_COMPONENT_CHANGE'; payload: boolean }

  // Cache Actions
  | { type: 'ADD_TO_VALIDATION_CACHE'; payload: { key: string; result: CompatibilityCheckResult } }
  | { type: 'ADD_TO_REPORT_CACHE'; payload: { key: string; report: CompatibilityReport } }
  | { type: 'CLEAR_VALIDATION_CACHE' }
  | { type: 'CLEAR_REPORT_CACHE' }
  | { type: 'REMOVE_FROM_VALIDATION_CACHE'; payload: string }
  | { type: 'REMOVE_FROM_REPORT_CACHE'; payload: string }

  // Performance Actions
  | { type: 'UPDATE_CHECK_PERFORMANCE'; payload: { duration: number } }
  | { type: 'INCREMENT_CHECKS_PERFORMED' }

/**
 * Initial compatibility state
 */
const initialCompatibilityState: CompatibilityState = {
  configuration: {},
  compatibilityResult: null,
  compatibilityReport: null,
  isChecking: false,
  checkError: null,
  autoCheck: true,
  checkOnComponentChange: true,
  validationCache: new Map(),
  reportCache: new Map(),
  lastCheckDuration: null,
  checksPerformed: 0,
  compatibilityEngine: new UnifiedCompatibilityEngine()
}

/**
 * Compatibility reducer
 */
function compatibilityReducer(state: CompatibilityState, action: CompatibilityAction): CompatibilityState {
  switch (action.type) {
    // Configuration Cases
    case 'SET_COMPONENT': {
      const { componentType, component } = action.payload
      let newConfiguration: ComponentConfiguration

      if (componentType === 'gpu' || componentType === 'memory' || componentType === 'storage') {
        // Handle array components
        const currentArray = (state.configuration[componentType] as { id: string }[]) || []
        newConfiguration = {
          ...state.configuration,
          [componentType]: [...currentArray, component]
        }
      } else {
        // Handle single components
        newConfiguration = {
          ...state.configuration,
          [componentType]: component
        }
      }

      return {
        ...state,
        configuration: newConfiguration,
        compatibilityResult: null // Clear previous result when configuration changes
      }
    }

    case 'REMOVE_COMPONENT': {
      const { componentType: removeType, componentId } = action.payload
      const updatedConfiguration: ComponentConfiguration = { ...state.configuration }

      if (removeType === 'gpu' || removeType === 'memory' || removeType === 'storage') {
        // Handle array components
        if (componentId && updatedConfiguration[removeType]) {
          updatedConfiguration[removeType] = (updatedConfiguration[removeType] as { id: string }[])
            .filter((item: { id: string }) => item.id !== componentId)
        } else {
          updatedConfiguration[removeType] = []
        }
      } else {
        // Handle single components
        delete updatedConfiguration[removeType]
      }

      return {
        ...state,
        configuration: updatedConfiguration,
        compatibilityResult: null
      }
    }

    case 'CLEAR_CONFIGURATION':
      return {
        ...state,
        configuration: {},
        compatibilityResult: null
      }

    case 'LOAD_CONFIGURATION':
      return {
        ...state,
        configuration: action.payload,
        compatibilityResult: null
      }

    // Compatibility Check Cases
    case 'START_COMPATIBILITY_CHECK':
      return {
        ...state,
        isChecking: true,
        checkError: null
      }

    case 'SET_COMPATIBILITY_RESULT':
      return {
        ...state,
        compatibilityResult: action.payload,
        isChecking: false,
        checkError: null,
        checksPerformed: state.checksPerformed + 1
      }

    case 'SET_COMPATIBILITY_ERROR':
      return {
        ...state,
        checkError: action.payload,
        isChecking: false
      }

    case 'CLEAR_COMPATIBILITY_RESULT':
      return {
        ...state,
        compatibilityResult: null,
        checkError: null
      }

    // Settings Cases
    case 'SET_AUTO_CHECK':
      return {
        ...state,
        autoCheck: action.payload
      }

    case 'SET_CHECK_ON_COMPONENT_CHANGE':
      return {
        ...state,
        checkOnComponentChange: action.payload
      }

    // Cache Cases
    case 'ADD_TO_VALIDATION_CACHE': {
      const newCache = new Map(state.validationCache)
      newCache.set(action.payload.key, action.payload.result)

      // Limit cache size to prevent memory issues
      if (newCache.size > 100) {
        const firstKey = newCache.keys().next().value
        if (firstKey !== undefined) {
          newCache.delete(firstKey)
        }
      }

      return {
        ...state,
        validationCache: newCache
      }
    }

    case 'CLEAR_VALIDATION_CACHE':
      return {
        ...state,
        validationCache: new Map()
      }

    case 'REMOVE_FROM_VALIDATION_CACHE': {
      const updatedCache = new Map(state.validationCache)
      updatedCache.delete(action.payload)
      return {
        ...state,
        validationCache: updatedCache
      }
    }

    // Performance Cases
    case 'UPDATE_CHECK_PERFORMANCE':
      return {
        ...state,
        lastCheckDuration: action.payload.duration
      }

    case 'INCREMENT_CHECKS_PERFORMED':
      return {
        ...state,
        checksPerformed: state.checksPerformed + 1
      }

    default:
      return state
  }
}

/**
 * Compatibility context interface
 */
interface CompatibilityContextType {
  state: CompatibilityState
  dispatch: React.Dispatch<CompatibilityAction>

  // Component Configuration
  setComponent: (componentType: keyof ComponentConfiguration, component: ComponentConfiguration[keyof ComponentConfiguration]) => void
  removeComponent: (componentType: keyof ComponentConfiguration, componentId?: string) => void
  clearConfiguration: () => void
  loadConfiguration: (config: ComponentConfiguration) => void

  // Compatibility Checking
  checkCompatibility: () => Promise<CompatibilityCheckResult>
  getCachedResult: (configKey: string) => CompatibilityCheckResult | undefined

  // Utility Functions
  generateConfigurationKey: (config: ComponentConfiguration) => string
  hasCompatibilityIssues: () => boolean
  getIssuesBySeverity: (severity: CompatibilityIssueSeverity) => CompatibilityIssue[]

  // Settings
  setAutoCheck: (enabled: boolean) => void
  setCheckOnComponentChange: (enabled: boolean) => void
}

/**
 * Compatibility context
 */
const CompatibilityContext = createContext<CompatibilityContextType | undefined>(undefined)

/**
 * Compatibility provider component
 */
export function CompatibilityProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(compatibilityReducer, initialCompatibilityState)

  /**
   * Set component in configuration
   */
  const setComponent = (componentType: keyof ComponentConfiguration, component: ComponentConfiguration[keyof ComponentConfiguration]) => {
    dispatch({ type: 'SET_COMPONENT', payload: { componentType, component } })
  }

  /**
   * Remove component from configuration
   */
  const removeComponent = (componentType: keyof ComponentConfiguration, componentId?: string) => {
    dispatch({
      type: 'REMOVE_COMPONENT',
      payload: componentId !== undefined
        ? { componentType, componentId }
        : { componentType }
    })
  }

  /**
   * Clear entire configuration
   */
  const clearConfiguration = () => {
    dispatch({ type: 'CLEAR_CONFIGURATION' })
  }

  /**
   * Load configuration
   */
  const loadConfiguration = (config: ComponentConfiguration) => {
    dispatch({ type: 'LOAD_CONFIGURATION', payload: config })
  }

  /**
   * Generate configuration key for caching
   */
  const generateConfigurationKey = (config: ComponentConfiguration) => {
    const sortedConfig = Object.keys(config)
      .sort()
      .reduce((result: Record<string, unknown>, key) => {
        const value = config[key as keyof ComponentConfiguration]
        if (Array.isArray(value)) {
          result[key] = (value as { id: string }[]).map(item => item.id).sort()
        } else if (value) {
          result[key] = (value as { id: string }).id
        }
        return result
      }, {})

    return JSON.stringify(sortedConfig)
  }

  /**
   * Get cached compatibility result
   */
  const getCachedResult = (configKey: string) => {
    return state.validationCache.get(configKey)
  }

  /**
   * Check compatibility of current configuration
   */
  const checkCompatibility = (): CompatibilityCheckResult => {
    const startTime = performance.now()
    dispatch({ type: 'START_COMPATIBILITY_CHECK' })

    try {
      const configKey = generateConfigurationKey(state.configuration)

      // Check cache first
      const cachedResult = getCachedResult(configKey)
      if (cachedResult) {
        dispatch({ type: 'SET_COMPATIBILITY_RESULT', payload: cachedResult })
        return cachedResult
      }

      // Perform compatibility check
      const result = performCompatibilityCheck(state.configuration)

      // Cache the result
      dispatch({ type: 'ADD_TO_VALIDATION_CACHE', payload: { key: configKey, result } })
      dispatch({ type: 'SET_COMPATIBILITY_RESULT', payload: result })

      // Update performance metrics
      const duration = performance.now() - startTime
      dispatch({ type: 'UPDATE_CHECK_PERFORMANCE', payload: { duration } })

      return result
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown compatibility check error'
      dispatch({ type: 'SET_COMPATIBILITY_ERROR', payload: errorMessage })
      throw error
    }
  }

  /**
   * Perform actual compatibility validation
   */
  const performCompatibilityCheck = (config: ComponentConfiguration): CompatibilityCheckResult => {
    const issues: CompatibilityIssue[] = []
    const warnings: CompatibilityIssue[] = []
    const recommendations: CompatibilityIssue[] = []

    // Basic validation - ensure we have at least some components
    if (Object.keys(config).length === 0) {
      issues.push({
        id: 'empty-config',
        type: 'configuration',
        severity: 'info',
        title: 'No components selected',
        description: 'Please select components to check compatibility',
        components: [],
        canProceed: true
      })
    }

    // CPU-Motherboard socket compatibility
    if (config.cpu && config.motherboard) {
      const motherboardSocket = typeof config.motherboard.cpuSocket === 'string'
        ? config.motherboard.cpuSocket
        : config.motherboard.cpuSocket?.type || '';

      if (config.cpu.socket !== motherboardSocket) {
        issues.push({
          id: 'cpu-motherboard-socket',
          type: 'socket-compatibility',
          severity: 'error',
          title: 'CPU and Motherboard socket mismatch',
          description: `CPU socket ${config.cpu.socket} is not compatible with motherboard socket ${motherboardSocket}`,
          components: ['cpu', 'motherboard'],
          suggestion: 'Select a CPU and motherboard with matching sockets',
          canProceed: false
        })
      }
    }

    // Memory compatibility checks
    if (config.memory && config.memory.length > 0 && config.motherboard) {
      const memoryTypes = [...new Set(config.memory.map(mem => mem.type))]

      if (memoryTypes.length > 1) {
        warnings.push({
          id: 'mixed-memory-types',
          type: 'memory-compatibility',
          severity: 'warning',
          title: 'Mixed memory types detected',
          description: 'Using different memory types may cause compatibility issues',
          components: ['memory'],
          suggestion: 'Use memory modules of the same type for best compatibility',
          canProceed: true
        })
      }
    }

    // Power supply wattage check
    if (config.psu && (config.cpu || config.gpu)) {
      let estimatedPower = 0

      if (config.cpu) {
        estimatedPower += config.cpu.tdp || 65
      }

      if (config.gpu && config.gpu.length > 0) {
        estimatedPower += config.gpu.reduce((sum, gpu) => sum + (gpu.powerAndThermal?.thermalDesignPower ?? 150), 0)
      }

      // Add overhead for other components
      estimatedPower += 100

      if (config.psu.wattage < estimatedPower) {
        issues.push({
          id: 'insufficient-power',
          type: 'power-compatibility',
          severity: 'error',
          title: 'Insufficient power supply',
          description: `Estimated power consumption (${estimatedPower}W) exceeds PSU capacity (${config.psu.wattage}W)`,
          components: ['psu', 'cpu', 'gpu'],
          suggestion: `Consider a PSU with at least ${Math.ceil(estimatedPower * 1.2)}W capacity`,
          canProceed: false
        })
      } else if (config.psu.wattage < estimatedPower * 1.2) {
        warnings.push({
          id: 'tight-power-margin',
          type: 'power-compatibility',
          severity: 'warning',
          title: 'Tight power supply margin',
          description: `PSU capacity is close to estimated power consumption`,
          components: ['psu'],
          suggestion: 'Consider a higher wattage PSU for better efficiency and future upgrades',
          canProceed: true
        })
      }
    }

    const errorCount = issues.filter(issue => issue.severity === 'error').length
    const isCompatible = errorCount === 0

    // Calculate compatibility score
    const maxScore = 100
    const warningPenalty = warnings.length * 5
    const errorPenalty = errorCount * 25
    const score = Math.max(0, maxScore - warningPenalty - errorPenalty)

    return {
      isCompatible,
      score,
      issues,
      warnings,
      recommendations
    }
  }

  /**
   * Check if current configuration has compatibility issues
   */
  const hasCompatibilityIssues = () => {
    if (!state.compatibilityResult) return false
    return state.compatibilityResult.issues.some(issue => issue.severity === 'error')
  }

  /**
   * Get issues by severity
   */
  const getIssuesBySeverity = (severity: CompatibilityIssueSeverity) => {
    if (!state.compatibilityResult) return []
    return state.compatibilityResult.issues.filter(issue => issue.severity === severity)
  }

  /**
   * Set auto check setting
   */
  const setAutoCheck = (enabled: boolean) => {
    dispatch({ type: 'SET_AUTO_CHECK', payload: enabled })
  }

  /**
   * Set check on component change setting
   */
  const setCheckOnComponentChange = (enabled: boolean) => {
    dispatch({ type: 'SET_CHECK_ON_COMPONENT_CHANGE', payload: enabled })
  }

  // Auto-check compatibility when configuration changes
  useEffect(() => {
    if (state.autoCheck && state.checkOnComponentChange && Object.keys(state.configuration).length > 0) {
      const timeoutId = setTimeout(() => {
        checkCompatibility().catch(console.error)
      }, 500) // Debounce checks

      return () => clearTimeout(timeoutId)
    }
    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.configuration, state.autoCheck, state.checkOnComponentChange])

  const contextValue: CompatibilityContextType = {
    state,
    dispatch,
    setComponent,
    removeComponent,
    clearConfiguration,
    loadConfiguration,
    checkCompatibility,
    getCachedResult,
    generateConfigurationKey,
    hasCompatibilityIssues,
    getIssuesBySeverity,
    setAutoCheck,
    setCheckOnComponentChange
  }

  return (
    <CompatibilityContext.Provider value={contextValue}>
      {children}
    </CompatibilityContext.Provider>
  )
}

/**
 * Hook to use compatibility context
 */
export function useCompatibility() {
  const context = useContext(CompatibilityContext)
  if (context === undefined) {
    throw new Error('useCompatibility must be used within a CompatibilityProvider')
  }
  return context
}